/**
 * Theme Studio — applies a partner's resolved theme (app-backend
 * `resolvedTheme`, catalogue in service/branding/themeCatalogue.ts) to <html>:
 *
 *   - CSS variables (`--zv-radius-*`, `--zv-shadow-*`, `--zv-btn-*`, `--zv-card-*`,
 *     `--zv-input-*`, `--zv-chip-radius`, `--zv-dialog-radius`, `--zv-canvas`, the
 *     font stacks and the neutral colour tokens) read by the SCSS with the shipped
 *     value as each var()'s fallback;
 *   - `data-zv-*` attributes for styles that need different rules rather than a
 *     different value: `btn-style` / `input-style` / `colors`, and one
 *     `data-zv-<component>-<prop>` per THEME_OPTIONS entry (read in SCSS with the
 *     `when-theme` mixin, in React with hooks/useThemeOption).
 *
 * ⚠️ A value equal to the shipped look is REMOVED, not written: the fallbacks are
 * the shipped look, so a partner on the Zenvoya preset renders exactly like one
 * with no theme. `null` clears everything this module ever set.
 */
import { parseHex } from "./partnerBranding";
import { palette } from "../styles/palette";
import { themed } from "../styles/design-tokens";
import { iconFilter } from "./iconFilter";
import { hostTenant } from "../branding/generated/brand-manifest";

export type ResolvedTheme = {
  preset: string;
  radius: string;
  elevation: string;
  fonts: { heading: string; body: string };
  colors: Partial<Record<ThemeColorKey, string>>;
  components: Record<string, Record<string, string>>;
};

type ThemeColorKey =
  | "canvas"
  | "surface"
  | "inset"
  | "highlight"
  | "overlay"
  | "textPrimary"
  | "textSecondary"
  | "textMuted"
  | "icon"
  | "onBrand"
  | "field"
  | "control"
  | "border"
  | "disabled"
  | "success"
  | "warning"
  | "error"
  | "info";

const RADIUS_STEPS = ["xs", "sm", "md", "lg", "xl"] as const;
const RADIUS_SCALES: Record<string, number[]> = {
  sharp: [0, 0, 0, 0, 0],
  crisp: [2, 2, 4, 4, 6],
  compact: [2, 4, 6, 8, 12],
  soft: [4, 6, 10, 14, 20],
  round: [6, 12, 16, 24, 32],
  tidy: [4, 6, 8, 12, 16],
};

const SHADOW_STEPS = ["xs", "sm", "md", "lg", "xl", "xxl"] as const;
const SHADOW_SCALES: Record<string, string[]> = {
  flat: ["none", "none", "none", "none", "none", "none"],
  subtle: [
    `0 1px 2px ${palette["black-a4"]}`,
    `0 1px 3px ${palette["black-a6"]}`,
    `0 2px 6px ${palette["black-a6"]}`,
    `0 4px 12px ${palette["black-a6"]}`,
    `0 8px 20px ${palette["black-a8"]}`,
    `0 12px 28px ${palette["black-a8"]}`,
  ],
  deep: [
    `0 2px 4px ${palette["black-a10"]}`,
    `0 4px 10px ${palette["black-a14"]}`,
    `0 8px 20px ${palette["black-a16"]}`,
    `0 14px 32px ${palette["black-a18"]}`,
    `0 28px 56px -8px ${palette["black-a24"]}`,
    `0 36px 72px -10px ${palette["black-a26"]}`,
  ],
  // Borders do the work on cards; pop-ups get one soft shadow, floating lists a deeper one.
  hairline: [
    "none",
    `0 4px 16px ${palette["gray-10-a8"]}`,
    `0 8px 16px ${palette["black-a4"]}`,
    `0 4px 16px ${palette["gray-10-a8"]}`,
    `0 16px 24px 2px ${palette["black-a14"]}`,
    `0 16px 24px 2px ${palette["black-a14"]}`,
  ],
};

/** Theme colour -> the design tokens it repaints (tokens.json names). */
const COLOR_TOKENS: Record<ThemeColorKey, string[]> = {
  canvas: ["zv-canvas"],
  surface: ["zv-surface"],
  inset: ["zv-well"],
  highlight: ["zv-highlight", "selected-tint"],
  overlay: ["zv-overlay"],
  icon: ["zv-icon"],
  onBrand: ["zv-on-brand", "btn-active"],
  field: ["zv-field"],
  control: ["zv-control"],
  disabled: ["zv-disabled", "btn-disabled-background", "btn-disabled-background-light"],
  border: ["zv-border", "other-border", "other-divider", "other-right-border"],
  textPrimary: ["zv-text-primary", "text-primary", "text-dark-charcoal", "text-black", "text-dark-blue", "action-active", "sel-con-active-text", "sel-con-selected-text"],
  textSecondary: ["zv-text-secondary", "text-secondary"],
  textMuted: ["zv-text-muted", "text-tertiary"],
  success: ["zv-success", "system-success", "text-green"],
  warning: ["zv-warning", "system-warning"],
  error: ["zv-error", "system-error", "text-red"],
  info: ["zv-info", "system-information"],
};

/**
 * Status colours whose light tint (`--system-*-light`) backs a status box. The
 * `--zv-*-tint` twins exist for hard-coded tints: `--system-*-light` is always
 * defined (tenant tokens), so only a var that is unset on the shipped look can
 * carry a fallback.
 */
const STATUS_TINTS = ["success", "warning", "error", "information"] as const;
const STATUS_ALIAS: Record<(typeof STATUS_TINTS)[number], string> = { success: "success", warning: "warning", error: "error", information: "info" };

/** Theme Studio fonts (app-backend BRANDING_FONTS) -> stylesheet. */
const FONT_SOURCES: Record<string, string> = {
  Satoshi: "https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700&display=swap",
};
const googleFont = (family: string) =>
  `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, "+")}:wght@400;500;600;700&display=swap`;
const SAFE_FONT = /^[A-Za-z0-9 ]+$/;

/**
 * Theme Studio options the app reads as `data-zv-<component>-<prop>` on <html>
 * (lower-cased: `data-zv-searchform-layout`), with the values it implements.
 * "default" (As shipped) — and any value not listed — writes nothing, so an
 * option a newer catalogue adds stays inert until the app implements it. One
 * table, so clearing never misses an attribute and hooks/useThemeOption reads
 * the same names. SCSS gates on them with `when-theme` (_theme-options.scss).
 */
export const THEME_OPTIONS = {
  tabs: { style: ["pill", "underline", "segmented"] },
  chip: { style: ["soft", "neutral", "ring"], ringWidth: ["thin", "medium", "thick"] },
  header: { style: ["light", "brand", "dark"], divider: ["line", "shadow", "none"] },
  bottomNav: { style: ["brand", "dark"], indicator: ["pill"] },
  button: { confirm: ["dark"] },
  card: { hover: ["border"] },
  input: { focus: ["dark", "accent"] },
  menu: { selectedMark: ["tick", "filled", "radio"] },
  dialog: { scrim: ["none", "light", "medium", "standard"], detailsPanel: ["sideSheet", "sideSheetOpen"] },
  typography: { headingWeight: ["medium", "semibold", "bold"] },
  selection: { color: ["secondary", "accent", "text"], links: ["accent", "info"] },
  searchTabs: { style: ["iconPill", "iconTop"] },
  searchForm: { layout: ["outlinedBoxes", "pillBar"], heading: ["pageTitle", "hero"], swap: ["circle"], button: ["floating"] },
  tripType: { desktop: ["radios"] },
  picker: { stepper: ["circle"], suggestions: ["codeChip"] },
  datePicker: { selection: ["block", "circles"], selectionColor: ["secondary", "accent"], months: ["two"], phone: ["bottomSheet", "dateBoxes"], dayWeight: ["regular", "medium", "semibold"] },
  filters: { bar: ["pills", "rail"], header: ["titleLeft"], footer: ["fullWidth"], sectionTitles: ["caps"], slider: ["darkTrack"] },
  sort: { flights: ["columns", "floatingSelect"], staysActivities: ["pillMenu", "floatingSelect"] },
  resultsSummary: { style: ["bar", "fieldBoxes"], heading: ["showing"] },
  resultsList: { hotelLayout: ["grid"], mobileBackground: ["tinted"], roundTrip: ["splitColumns"], flightList: ["joined"] },
  rating: { style: ["tint"], stars: ["dark"] },
  badge: { style: ["pastel"], case: ["uppercase"] },
  myTripsDetail: { headers: ["iconCards", "cleanList", "solidIcons"] },
  staysMap: {
    layout: ["tile"],
    pins: ["ink", "brand"],
    card: ["list", "docked"],
    cardSize: ["medium", "large"],
    style: ["muted", "dark"],
    controls: ["stack"],
  },
} as const;

type ThemeOptionTable = typeof THEME_OPTIONS;
export type ThemeOptionComponent = keyof ThemeOptionTable;
export type ThemeOptionProp<C extends ThemeOptionComponent> = keyof ThemeOptionTable[C] & string;
/** "default" or one of the values the app implements for that prop. */
export type ThemeOptionValue<C extends ThemeOptionComponent, P extends ThemeOptionProp<C>> =
  | "default"
  | (ThemeOptionTable[C][P] extends ReadonlyArray<infer V> ? V : never);

const PREFIX_ATTR = "data-zv-";
const optionAttr = (component: string, prop: string) => `${component}-${prop}`.toLowerCase();
/** The <html> attribute that carries one THEME_OPTIONS prop. */
export const themeAttr = (component: ThemeOptionComponent, prop: string): string => PREFIX_ATTR + optionAttr(component, prop);

const OPTION_ENTRIES = (Object.entries(THEME_OPTIONS) as Array<[string, Record<string, readonly string[]>]>).flatMap(([component, props]) =>
  Object.entries(props).map(([prop, values]) => ({ component, prop, values, attr: optionAttr(component, prop) }))
);
const ATTRS = ["btn-style", "input-style", "colors", ...OPTION_ENTRIES.map((o) => o.attr)];

/** `cN` corner sizes (catalogue CORNERS) -> the component radius vars they set, in exact px. */
const CORNER_VARS: Record<string, string[]> = {
  button: ["--zv-btn-radius", "--zv-btn-radius-mui"],
  card: ["--zv-card-radius"],
  input: ["--zv-input-radius"],
  chip: ["--zv-chip-radius"],
  menu: ["--zv-menu-radius"],
  dialog: ["--zv-dialog-radius"],
  picker: ["--zv-picker-radius"],
};
const CORNER = /^c(\d{1,2})$/;

// The partner's Secondary and Accent colours (partnerBranding PARTNER_TOKEN_ROLES
// `secondary` / `accent`), and the theme's text colour.
const SECONDARY = "var(--accent-main)";
const ACCENT = "var(--secondary-bright)";
const TEXT = `var(--zv-text-primary, ${palette["Neutral-Darkest"]})`;

/** Value-driven options: component.prop -> value -> { custom property: value }. */
const OPTION_VARS: Record<string, Record<string, Record<string, Record<string, string>>>> = {
  button: { confirm: { dark: { "--zv-confirm-bg": SECONDARY } } },
  // The resting border mixed toward the text colour.
  card: { hover: { border: { "--zv-card-hover-border": `color-mix(in srgb, var(--zv-border, ${palette["black-a15"]}) 60%, ${TEXT})` } } },
  input: { focus: { dark: { "--zv-input-focus": TEXT }, accent: { "--zv-input-focus": ACCENT } } },
  chip: { ringWidth: { thin: { "--zv-chip-ring-width": "1px" }, medium: { "--zv-chip-ring-width": "1.5px" }, thick: { "--zv-chip-ring-width": "2px" } } },
  menu: { highlight: { highlight: { "--zv-menu-hover-bg": "var(--zv-highlight, var(--secondary-main))" } } },
  typography: { headingWeight: { medium: { "--zv-heading-weight": "500" }, semibold: { "--zv-heading-weight": "600" }, bold: { "--zv-heading-weight": "700" } } },
  selection: {
    color: { secondary: { "--zv-selection": SECONDARY }, accent: { "--zv-selection": ACCENT }, text: { "--zv-selection": TEXT } },
    links: { accent: { "--zv-link": ACCENT }, info: { "--zv-link": "var(--zv-info, var(--system-information))" } },
  },
  datePicker: {
    selectionColor: { secondary: { "--zv-date-selection": SECONDARY }, accent: { "--zv-date-selection": ACCENT } },
    dayWeight: { regular: { "--zv-date-day-weight": "400" }, medium: { "--zv-date-day-weight": "500" }, semibold: { "--zv-date-day-weight": "600" } },
  },
};

/** Dialog scrim strength: how much of the overlay colour covers the page. */
const SCRIM_STRENGTH: Record<string, string> = { none: "0%", light: "20%", medium: "40%", standard: "60%" };

/** Every custom property this module may write, so clearing never misses one. */
const OWNED_PROPS = new Set<string>([
  ...RADIUS_STEPS.map((s) => `--zv-radius-${s}`),
  ...SHADOW_STEPS.map((s) => `--zv-shadow-${s}`),
  "--zv-btn-radius",
  "--zv-btn-radius-mui",
  "--zv-btn-weight",
  "--zv-btn-case",
  "--zv-card-radius",
  "--zv-card-border",
  "--zv-card-shadow",
  "--zv-card-bg",
  "--zv-input-radius",
  "--zv-input-border-width",
  "--zv-input-bg",
  "--zv-chip-radius",
  "--zv-dialog-radius",
  "--zv-picker-radius",
  "--zv-scrim-strength",
  "--zv-menu-radius",
  "--zv-menu-border",
  "--zv-menu-shadow",
  "--zv-menu-bg",
  "--zv-menu-hover-bg",
  "--zv-wrapper-bg",
  "--zv-well-hover",
  "--zv-icon-filter",
  "--zv-shimmer",
  "--selected-strong",
  "color-scheme",
  ...STATUS_TINTS.map((s) => `--system-${s}-light`),
  ...STATUS_TINTS.map((s) => `--zv-${STATUS_ALIAS[s]}-tint`),
  "--brand-font-heading",
  "--brand-font-body",
  "--zv-font-heading",
  ...Object.values(OPTION_VARS).flatMap((props) =>
    Object.values(props).flatMap((values) => Object.values(values).flatMap((decls) => Object.keys(decls)))
  ),
  ...Object.values(COLOR_TOKENS).flatMap((names) => names.flatMap((n) => [`--${n}`, `--${n}-rgb`])),
]);

const loadFont = (family: string): void => {
  if (!SAFE_FONT.test(family)) return;
  const href = FONT_SOURCES[family] ?? googleFont(family);
  if (document.head.querySelector(`link[data-zv-theme-font="${family}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.setAttribute("data-zv-theme-font", family);
  document.head.appendChild(link);
};

const fontStack = (family: string) => `'${family}', sans-serif`;

/** The CSS properties and attributes one theme asks for. Absent = shipped look. */
const themeDeclarations = (theme: ResolvedTheme) => {
  const props: Record<string, string> = {};
  const attrs: Record<string, string> = {};
  const c = theme.components || {};
  const r = (step: string, fallback: string) => `var(--zv-radius-${step}, ${fallback})`;

  const radii = RADIUS_SCALES[theme.radius];
  if (radii) RADIUS_STEPS.forEach((step, i) => (props[`--zv-radius-${step}`] = `${radii[i]}px`));
  const shadows = SHADOW_SCALES[theme.elevation];
  if (shadows) SHADOW_STEPS.forEach((step, i) => (props[`--zv-shadow-${step}`] = shadows[i]));

  // A brand pack owns the fonts (assets and fonts are all it supplies), so the theme's are not applied.
  if (!hostTenant && theme.fonts?.heading && SAFE_FONT.test(theme.fonts.heading)) {
    props["--brand-font-heading"] = fontStack(theme.fonts.heading);
    // Most titles ship in the BODY font (the app's own headings predate the token),
    // so they read this one var, set only for a themed partner: a partner's
    // Headings font reaches every title while an unthemed app stays as it ships.
    props["--zv-font-heading"] = fontStack(theme.fonts.heading);
  }
  if (!hostTenant && theme.fonts?.body && SAFE_FONT.test(theme.fonts.body)) props["--brand-font-body"] = fontStack(theme.fonts.body);

  // Exact corner sizes first, so a style that forces its own corners (underline
  // inputs) still wins below.
  for (const [component, names] of Object.entries(CORNER_VARS)) {
    const px = CORNER.exec(c[component]?.shape ?? "")?.[1];
    if (px) names.forEach((name) => (props[name] = `${px}px`));
  }

  // Buttons — shipped: pill, solid, medium, as written.
  if (c.button?.shape === "square") props["--zv-btn-radius"] = r("xs", "4px");
  if (c.button?.shape === "rounded") props["--zv-btn-radius"] = r("sm", "8px");
  // MUI <Button>s ship at 4px, not pill, so "pill" leaves them alone (App.tsx theme).
  if (c.button?.shape === "square" || c.button?.shape === "rounded") props["--zv-btn-radius-mui"] = props["--zv-btn-radius"];
  if (c.button?.weight === "semibold") props["--zv-btn-weight"] = "600";
  if (c.button?.weight === "bold") props["--zv-btn-weight"] = "700";
  if (c.button?.case === "uppercase" || c.button?.case === "capitalize") props["--zv-btn-case"] = c.button.case;
  if (c.button?.style === "outline" || c.button?.style === "soft") attrs["btn-style"] = c.button.style;

  // Cards — shipped: outlined, lg corners, no shadow.
  if (c.card?.shape === "square") props["--zv-card-radius"] = r("xs", "4px");
  if (c.card?.shape === "round") props["--zv-card-radius"] = r("xl", "24px");
  if (c.card?.style === "elevated") {
    props["--zv-card-border"] = "1px solid transparent";
    props["--zv-card-shadow"] = `var(--zv-shadow-sm, 0 2px 4px -2px ${palette["black-a6"]}, 0 4px 8px -2px ${palette["black-a10"]})`;
  }
  if (c.card?.style === "flat") props["--zv-card-border"] = "1px solid transparent";
  if (c.card?.style === "tinted") {
    props["--zv-card-border"] = "1px solid transparent";
    props["--zv-card-bg"] = "rgba(var(--primary-main-rgb), 0.06)";
  }

  // Inputs — shipped: outlined, sm corners.
  if (c.input?.shape === "square") props["--zv-input-radius"] = r("xs", "4px");
  if (c.input?.shape === "pill") props["--zv-input-radius"] = "200px";
  // The attribute gates the background override, so an input that never had one
  // (and keeps the browser's own, e.g. disabled grey) is untouched by "outlined".
  if (c.input?.style === "filled" || c.input?.style === "underline") attrs["input-style"] = c.input.style;
  // Filled keeps its full 1px border: near-invisible on the grey fill, and it is
  // what turns brand-coloured on focus. A bottom-only line looks broken on pills.
  if (c.input?.style === "filled") props["--zv-input-bg"] = "var(--bg-white-smoke)";
  if (c.input?.style === "underline") {
    props["--zv-input-bg"] = "transparent";
    props["--zv-input-border-width"] = "0 0 1px";
    props["--zv-input-radius"] = "0";
  }

  // Tabs, chip, header and bottom-nav styles are THEME_OPTIONS attributes. Tabs
  // "default" (As shipped) leaves every tab bar as it ships; a chosen style applies
  // to all of them — "pill" too, since My Trips' tab bar ships underlined.

  // Chips — shipped: outline, pill.
  if (c.chip?.shape === "square") props["--zv-chip-radius"] = r("xs", "4px");
  if (c.chip?.shape === "rounded") props["--zv-chip-radius"] = r("sm", "8px");

  // Dropdown menus — "default" leaves every menu as it ships (they differ per
  // screen). "card" is the travellers picker's panel (customDropDown .cardStyles).
  if (c.menu?.shape === "square") props["--zv-menu-radius"] = r("xs", "4px");
  if (c.menu?.shape === "rounded") props["--zv-menu-radius"] = r("sm", "8px");
  if (c.menu?.shape === "round") props["--zv-menu-radius"] = r("lg", "16px");
  if (c.menu?.style === "card") {
    props["--zv-menu-border"] = `1px solid ${palette["black-a15"]}`;
    props["--zv-menu-shadow"] = `0px 8px 8px -4px ${palette["black-a3-1"]}`;
    props["--zv-menu-bg"] = palette["gray-99"];
  }
  if (c.menu?.style === "elevated") {
    props["--zv-menu-border"] = "1px solid transparent";
    props["--zv-menu-shadow"] = `var(--zv-shadow-lg, 0 8px 8px -4px ${palette["black-a3"]}, 0 20px 24px -4px ${palette["black-a8"]})`;
  }
  if (c.menu?.style === "outlined") {
    props["--zv-menu-border"] = `1px solid ${palette["black-a15"]}`;
    props["--zv-menu-shadow"] = "none";
  }
  if (c.menu?.highlight === "neutral") props["--zv-menu-hover-bg"] = "var(--bg-white-smoke)";
  if (c.menu?.highlight === "tinted") props["--zv-menu-hover-bg"] = "rgba(var(--primary-main-rgb), 0.08)";

  if (c.dialog?.shape === "square") props["--zv-dialog-radius"] = r("xs", "4px");
  // "rounded" is the Zenvoya preset's value, so it must leave dialogs as shipped.
  if (c.dialog?.shape === "round") props["--zv-dialog-radius"] = r("xl", "24px");

  // Every implemented non-default option: its attribute, and the values it sets.
  for (const { component, prop, values, attr } of OPTION_ENTRIES) {
    const value = c[component]?.[prop];
    if (value && values.includes(value)) attrs[attr] = value;
  }
  for (const [component, byProp] of Object.entries(OPTION_VARS)) {
    for (const [prop, byValue] of Object.entries(byProp)) Object.assign(props, byValue[c[component]?.[prop] ?? ""]);
  }

  for (const [key, names] of Object.entries(COLOR_TOKENS) as Array<[ThemeColorKey, string[]]>) {
    const hex = theme.colors?.[key];
    const rgb = hex ? parseHex(hex) : null;
    if (!hex || !rgb) continue;
    for (const name of names) {
      props[`--${name}`] = hex;
      props[`--${name}-rgb`] = rgb.join(", ");
    }
  }
  // A surface colour reaches the result cards unless a tinted card overrides it.
  if (props["--zv-surface"] && !props["--zv-card-bg"]) props["--zv-card-bg"] = "var(--zv-surface)";
  // White that only exists to blend into a white parent (an accordion's Paper
  // wrapper, a borderless dropdown trigger) turns see-through once the page or
  // card colour can differ, so it shows whatever it sits on.
  if (props["--zv-canvas"] || props["--zv-surface"]) props["--zv-wrapper-bg"] = "transparent";
  // Light-grey insets (a ticket's header bar, a note box) take the partner's
  // "Insets & panels" colour. Left empty while other neutrals are set, they are
  // a faint tint of the text colour over whatever is behind them, so they stay a
  // step off the page / card in any palette — light-grey on a dark theme would
  // sit behind light text.
  if (!props["--zv-well"] && (props["--zv-canvas"] || props["--zv-surface"] || props["--zv-text-primary"])) {
    props["--zv-well"] = `color-mix(in srgb, var(--zv-text-primary, ${palette["Neutral-Darkest"]}) 6%, transparent)`;
  }
  // An inset's hover is one more faint text tint over it, so it still reads as a
  // change whatever the inset colour is.
  if (props["--zv-well"]) props["--zv-well-hover"] = `color-mix(in srgb, var(--zv-text-primary, ${palette["Neutral-Darkest"]}) 6%, var(--zv-well))`;
  // Dropdown menus are panels too: on a partner's Cards & sheets colour they take
  // it (the "card" style's near-white included) and their grey hover becomes the
  // inset colour, so a dark palette never puts light text on a white menu.
  if (props["--zv-surface"]) {
    props["--zv-menu-bg"] = "var(--zv-surface)";
    if (!props["--zv-menu-hover-bg"] || props["--zv-menu-hover-bg"] === "var(--bg-white-smoke)") {
      props["--zv-menu-hover-bg"] = "var(--zv-well)";
    }
  }

  // Colours a partner leaves empty follow the ones they did set, so a partial
  // palette stays coherent. Input fields and buttons & chips ship white on white
  // cards, so they take the card colour.
  if (!props["--zv-field"] && props["--zv-surface"]) props["--zv-field"] = "var(--zv-surface)";
  if (!props["--zv-control"] && props["--zv-surface"]) props["--zv-control"] = "var(--zv-surface)";
  // Icons ship in a text grey.
  if (!props["--zv-icon"] && (props["--zv-text-secondary"] || props["--zv-text-primary"])) {
    props["--zv-icon"] = props["--zv-text-secondary"] ? "var(--zv-text-secondary)" : "var(--zv-text-primary)";
  }
  // Icon IMAGES cannot take a colour, only a filter computed from it.
  const iconHex = theme.colors?.icon || theme.colors?.textSecondary || theme.colors?.textPrimary;
  const iconRgb = iconHex ? parseHex(iconHex) : null;
  if (iconRgb) props["--zv-icon-filter"] = iconFilter(iconRgb);
  // Disabled fills ship as a light grey: a faint step of the text colour instead.
  if (!props["--zv-disabled"] && props["--zv-text-primary"]) {
    props["--zv-disabled"] = `color-mix(in srgb, var(--zv-text-primary) 16%, var(--zv-surface, ${palette["White"]}))`;
  }
  // Selected & highlights ship as a pale tint of the brand made for white: on a
  // partner's card colour it is the brand mixed into that colour instead.
  if (!props["--zv-highlight"] && props["--zv-surface"]) {
    props["--zv-highlight"] = "color-mix(in srgb, var(--primary-main) 16%, var(--zv-surface))";
    props["--selected-tint"] = "var(--zv-highlight)";
  }
  if (props["--zv-highlight"]) props["--selected-strong"] = "color-mix(in srgb, var(--primary-main) 30%, var(--zv-highlight))";
  // Status boxes (success / warning / error / info tints) sit on the card colour.
  // Hard-coded status colours read --zv-success / -warning / -error / -info
  // (set above only when the partner picks one) and --zv-*-tint.
  if (props["--zv-surface"]) {
    STATUS_TINTS.forEach((s) => {
      const tint = `color-mix(in srgb, var(--system-${s}) 16%, var(--zv-surface))`;
      props[`--system-${s}-light`] = tint;
      props[`--zv-${STATUS_ALIAS[s]}-tint`] = tint;
    });
  }
  // An overlay dims what is behind a dialog, so it is never solid: the partner's
  // colour (or the shipped black) at the chosen Page dim, 60% for a colour alone.
  // "default" with no colour leaves each screen's own dim.
  const scrim = SCRIM_STRENGTH[c.dialog?.scrim ?? ""];
  const overlay = props["--zv-overlay"] ? theme.colors?.overlay : undefined;
  if (scrim) props["--zv-scrim-strength"] = scrim;
  if (overlay || scrim) {
    props["--zv-overlay"] = `color-mix(in srgb, ${overlay || palette["Neutral-Darkest"]} ${scrim || "60%"}, transparent)`;
  }
  // A dark page gets the browser's dark scrollbars, date inputs and autofill.
  const pageRgb = parseHex(theme.colors?.canvas || theme.colors?.surface || "");
  if (pageRgb && 0.2126 * pageRgb[0] + 0.7152 * pageRgb[1] + 0.0722 * pageRgb[2] < 110) {
    props["color-scheme"] = "dark";
    // Skeleton shimmer ships as 55% white, a bright streak on dark bones.
    props["--zv-shimmer"] = `color-mix(in srgb, var(--zv-text-primary, ${palette["White"]}) 10%, transparent)`;
  }

  // Input fields colour fills every input (outlined ones ship see-through on a
  // white card). A filled style keeps its step off the card unless the partner
  // chose the field colour; underline stays see-through.
  if (props["--zv-field"] && c.input?.style !== "underline") {
    props["--zv-input-bg"] = c.input?.style === "filled" && !theme.colors?.field ? "var(--zv-well)" : "var(--zv-field)";
    if (!attrs["input-style"]) attrs["input-style"] = "outlined";
  }

  // Which colour roles are set, for the global rules that reach markup no
  // stylesheet of ours owns (MUI, react-calendar, Google's place suggestions).
  const roles = [
    ["canvas", "--zv-canvas"],
    ["surface", "--zv-surface"],
    ["inset", "--zv-well"],
    ["highlight", "--zv-highlight"],
    ["overlay", "--zv-overlay"],
    ["text", "--zv-text-primary"],
    ["text-secondary", "--zv-text-secondary"],
    ["text-muted", "--zv-text-muted"],
    ["icon", "--zv-icon"],
    ["on-brand", "--zv-on-brand"],
    ["field", "--zv-field"],
    ["control", "--zv-control"],
    ["border", "--zv-border"],
    ["disabled", "--zv-disabled"],
  ]
    .filter(([, prop]) => props[prop])
    .map(([role]) => role);
  if (roles.length) attrs.colors = roles.join(" ");

  return { props, attrs };
};

/**
 * Apply (or, with `null`, clear) a resolved theme on <html>. Idempotent: every
 * owned property / attribute not asked for this time is removed.
 */
export const applyThemeToDocument = (theme: ResolvedTheme | null | undefined): void => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const { props, attrs } = theme ? themeDeclarations(theme) : { props: {}, attrs: {} };

  OWNED_PROPS.forEach((name) => {
    if (props[name] === undefined) root.style.removeProperty(name);
  });
  // COLOR_TOKENS never overlap partnerBranding's PARTNER_TOKEN_ROLES, so clearing
  // them here cannot undo the partner's primary palette.
  for (const [name, value] of Object.entries(props)) root.style.setProperty(name, value);

  for (const attr of ATTRS) {
    const value = attrs[attr];
    if (value) root.setAttribute(PREFIX_ATTR + attr, value);
    else root.removeAttribute(PREFIX_ATTR + attr);
  }

  if (!hostTenant && theme?.fonts?.heading) loadFont(theme.fonts.heading);
  if (!hostTenant && theme?.fonts?.body && theme.fonts.body !== theme.fonts.heading) loadFont(theme.fonts.body);
};

/** A `resolvedTheme` off the wire, or undefined when absent / not a theme. */
export const themeFromWire = (raw: unknown): ResolvedTheme | undefined => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const t = raw as Partial<ResolvedTheme>;
  if (typeof t.radius !== "string" || typeof t.elevation !== "string" || !t.components || typeof t.components !== "object") {
    return undefined;
  }
  return {
    preset: String(t.preset || ""),
    radius: t.radius,
    elevation: t.elevation,
    fonts: { heading: String(t.fonts?.heading || ""), body: String(t.fonts?.body || "") },
    colors: t.colors && typeof t.colors === "object" ? t.colors : {},
    components: t.components as ResolvedTheme["components"],
  };
};

/**
 * TS twins of the _theme-roles.scss option aliases, for `sx` / inline styles;
 * each falls back to the shipped brand colour.
 */
export const THEMED_INPUT_FOCUS = `var(--zv-input-focus, ${themed.brand})`;
export const THEMED_SELECTION = `var(--zv-selection, ${themed.brand})`;

/**
 * Theme Studio input style for MUI <TextField>s styled through `sx` (the flight
 * From / To boxes), which SCSS cannot reach. Spread into `.MuiOutlinedInput-root`;
 * each fallback is the shipped value. `fieldsetWidth` goes after every fieldset
 * `border` shorthand, which would otherwise reset the width.
 */
export const THEMED_MUI_INPUT_ROOT = {
  borderRadius: "var(--zv-input-radius, 8px)",
  backgroundColor: "var(--zv-input-bg, transparent)",
  // Input focus: a class selector, so it wins over a field's own `&.Mui-focused
  // fieldset` brand border whatever the spread order; the fallback is that brand.
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: THEMED_INPUT_FOCUS },
} as const;
export const THEMED_MUI_FIELDSET_WIDTH = "var(--zv-input-border-width, 1px)";
